// The APG ORION run CLIENT (CR-20260910-001 milestone 1, contract A1 v0.2 §8).
//
// The browser skill is gone: `RunApgOrionSkill` goes out over the shell-router command path (which
// returns an ack), `ApgOrionCardsReadyDto` comes back over the ESS WebSocket, correlated by runId.
// This file is the whole of what replaces about 1,150 lines of browser reasoning — the rules now
// live in the backend `ApgOrion` module, and Maven orders the same object in process.
//
// MODELLED ON `copilot-shared/backend-opg-trades-skill.ts`, deliberately and field for field where
// the shapes allow: it is the family's proven transport and it already carries the three rules that
// matter here.
//
// Transport rules (ADR-033):
//   - SUBSCRIBE BEFORE SEND. The push rides the ESS WebSocket while the command ack is a separate
//     HTTP call; subscribing first removes the "push beat the ack" race.
//   - GUARANTEED TEARDOWN. Every pending entry carries a hard teardown timer. Every settle path —
//     resolve, each reject, the timer, an ack failure, even processCommand throwing SYNCHRONOUSLY —
//     funnels through ONE choke point that unsubscribes, kills the timer and clears the map entry,
//     so no promise or subscription can outlive its run under ANY outcome.
//   - OUTCOME MAPPING (A1 §5): succeeded / empty resolve; superseded / terminalFailed /
//     preSendBlocked reject with a DISTINGUISHABLE tag. Late or foreign runId pushes are ignored.
//
// ⚠ ONE CLIENT, MANY PANELS. The DI-provided instance is module-level, so in-flight runs live in a
// pending-run MAP keyed by runId — never a single-flight slot. Each panel's own single-flight is
// the session's job, not this adapter's.
//
// ⚠ NO CANCEL, NO STOP (ruling R-D). Running again supersedes the run in flight and the displaced
// run receives `superseded` exactly once.
//
// ES5 note: arrays + indexOf only (no Set / Map iteration, no for..of), matching the sibling
// copilot logic; the pending map is a plain object keyed by runId.

import { Injectable } from '@angular/core';
import { Subscription } from 'rxjs';
import { getShortUUID, isValidNumber } from '../../utils';
import { MessageBusService } from '../../message-bus.service';
import { ShellClientService } from '../../shell-communication/shell-client.service';
import { CashFlowAdjustment } from '../model/CashFlowAdjustment';
import { CostDirection, RollSuggestionContent } from './apg-copilot-domain';
import { ApgOrionRunRequest, ApgOrionRunResult, IRollPositionsSkill } from './apg-roll-positions-skill';
import {
    APG_ORION_CARDS_READY_MESSAGE_TYPE,
    APG_ORION_CONTRACT_VERSION,
    APG_ORION_OUTCOME_EMPTY,
    APG_ORION_OUTCOME_PRE_SEND_BLOCKED,
    APG_ORION_OUTCOME_SUCCEEDED,
    APG_ORION_OUTCOME_SUPERSEDED,
    APG_ORION_OUTCOME_TERMINAL_FAILED,
    ApgOrionCardDto,
    ApgOrionCardsReadyDto,
    RunApgOrionSkill
} from './apg-orion-operations';

// Hard per-run teardown deadline. The backend budget is 40 s (A1 §7, A-G6); the grace guarantees
// the ORCHESTRATOR's own settlement always fires first, so the user-visible "took too long" belongs
// to the session. This timer is purely the resource reaper for runs nothing will ever push to — a
// donor-silent supersede, a dead shell.
export const APG_ORION_RUN_BUDGET_MS = 40000;
export const APG_ORION_TEARDOWN_GRACE_MS = 5000;
export const APG_ORION_TEARDOWN_MS = APG_ORION_RUN_BUDGET_MS + APG_ORION_TEARDOWN_GRACE_MS;

// ---- Distinguishable rejection ----------------------------------------------------------------
// A superseded rejection must be tellable apart from a terminal failure. ES5 target: Error
// subclassing mis-sets the prototype chain, so the marker is a tagged plain Error + type guards.
// Same mechanism as the OPG client's, for the same reason.

const REJECTION_OUTCOME_FIELD = 'apgOrionOutcome';

export function makeApgOrionRejection(outcome: string, message: string): Error {
    const e = new Error(message);
    (e as any)[REJECTION_OUTCOME_FIELD] = outcome;
    return e;
}

/** The outcome an ORION rejection carries; null for a foreign error. */
export function apgOrionRejectionOutcomeOf(e: any): string | null {
    return (e && typeof e[REJECTION_OUTCOME_FIELD] === 'string') ? e[REJECTION_OUTCOME_FIELD] : null;
}

export function isApgOrionSupersededRejection(e: any): boolean {
    return apgOrionRejectionOutcomeOf(e) === APG_ORION_OUTCOME_SUPERSEDED;
}

/**
 * The sentence a failed run puts on screen.
 *
 * ⚠ IT NEVER COLLAPSES TO "failed". A refusal is the backend saying the request was wrong, a
 * supersede is the trader having asked again, and a terminal failure is the platform. Three
 * different things to do next, so three different sentences — and the backend detail is preferred
 * over anything composed here, because the fact is known where it was written.
 */
export function describeApgOrionFailure(e: any): string {
    const detail = (e && e.message) ? String(e.message) : '';
    const outcome = apgOrionRejectionOutcomeOf(e);

    if (outcome === APG_ORION_OUTCOME_SUPERSEDED) {
        return detail || 'Superseded by a newer run from this panel.';
    }
    if (outcome === APG_ORION_OUTCOME_PRE_SEND_BLOCKED) {
        return detail
            ? 'The ORION run was refused: ' + detail
            : 'The ORION run was refused.';
    }
    return detail ? 'The ORION run failed: ' + detail : 'The ORION run failed.';
}

// ---- The client -------------------------------------------------------------------------------

interface PendingRun {
    subscription: Subscription;
    timerHandle: any;
    resolve: (result: ApgOrionRunResult) => void;
    reject: (error: Error) => void;
}

@Injectable()
export class BackendApgOrionSkill implements IRollPositionsSkill {

    // ⚠ TWO CONSTRUCTOR ARGUMENTS, BOTH INJECTABLE CLASSES. The class this replaces carries a
    // scar worth keeping in view: it took a defaulted PRIMITIVE-typed constructor parameter once,
    // Angular emitted a factory that injected index 0, got `ɵɵinvalidFactoryDep`, threw, and took
    // the whole ORION panel down at smoke. A default value does NOT exempt a parameter from DI.
    // Never add a primitive, a string union or an options object here.
    constructor(
        private readonly _messageBus: MessageBusService,
        private readonly _shellClient: ShellClientService
    ) {}

    // Pending-run MAP keyed by the client-minted runId. Plain object — ES5-safe, and the Karma
    // leak assertions read pendingRunCount after every settle path.
    private readonly _pending: { [runId: string]: PendingRun } = {};

    get pendingRunCount(): number {
        return Object.keys(this._pending).length;
    }

    run(request: ApgOrionRunRequest): Promise<ApgOrionRunResult> {
        return new Promise<ApgOrionRunResult>((resolve, reject) => {

            // Client-side short-circuit: a missing portfolio is a request the backend's pre-send
            // validator blocks deterministically (`MissingPortfolio`, A1 §3). Fail it here without a
            // round trip, a subscription or a map entry. This is a TRANSPORT guard on a transport
            // requirement, NOT questionnaire judgment — the rules stay on the backend.
            const portfolioId = request ? request.portfolioId : '';
            if (!portfolioId) {
                reject(makeApgOrionRejection(
                    APG_ORION_OUTCOME_PRE_SEND_BLOCKED, 'No portfolio is selected.'));
                return;
            }

            // The WIRE runId, echoed by the push. Minted HERE, per run, with the family convention:
            // the orchestrator's session run id is its own and never reaches this adapter.
            const runId = getShortUUID();

            // (1) SUBSCRIBE BEFORE SEND. Per-run subscription; a foreign runId falls through the
            // correlation check in onCardsReady.
            const subscription = this._messageBus
                .of<ApgOrionCardsReadyDto>(APG_ORION_CARDS_READY_MESSAGE_TYPE)
                .subscribe(m => this.onCardsReady(runId, m ? m.payload : null));

            // (2) the hard teardown timer — the reaper for runs nothing will ever push to.
            const timerHandle = setTimeout(() => {
                this.settle(runId, entry => entry.reject(makeApgOrionRejection(
                    APG_ORION_OUTCOME_TERMINAL_FAILED,
                    'The ORION run received no result within the teardown deadline.')));
            }, APG_ORION_TEARDOWN_MS);

            this._pending[runId] = { subscription, timerHandle, resolve, reject };

            // (3) send. The ack failing (rejected promise) or processCommand throwing SYNCHRONOUSLY
            // (e.g. an invalid session) both settle through the choke point — no path leaves the
            // entry behind.
            try {
                this._shellClient
                    .processCommand(buildRunApgOrionCommand(runId, request))
                    .catch((e: any) => {
                        const message = (e && e.message) ? String(e.message) : 'The ORION run could not be sent.';
                        this.settle(runId, entry => entry.reject(
                            makeApgOrionRejection(APG_ORION_OUTCOME_TERMINAL_FAILED, message)));
                    });
            } catch (e) {
                const message = (e && e.message) ? String(e.message) : 'The ORION run could not be sent.';
                this.settle(runId, entry => entry.reject(
                    makeApgOrionRejection(APG_ORION_OUTCOME_TERMINAL_FAILED, message)));
            }
        });
    }

    // ESS push handler for ONE pending run: correlate on runId (a late or foreign push is ignored,
    // and the entry stays untouched for its own push or its timer), then map the outcome per A1 §5.
    private onCardsReady(runId: string, dto: ApgOrionCardsReadyDto | null): void {
        if (!dto || dto.runId !== runId) {
            return;   // not this run's push
        }

        if (dto.outcome === APG_ORION_OUTCOME_SUCCEEDED || dto.outcome === APG_ORION_OUTCOME_EMPTY) {
            const result: ApgOrionRunResult = {
                contents: toSuggestionContents(dto.cards),
                priceBoxAdjustments: toPriceBoxAdjustments(dto),
                warnings: (dto.warnings || []).filter(w => !!w),
                empty: dto.outcome === APG_ORION_OUTCOME_EMPTY
            };
            this.settle(runId, entry => entry.resolve(result));
            return;
        }

        // superseded (distinguishable via the outcome tag) / terminalFailed / preSendBlocked — and
        // any future unknown outcome fails CLOSED as a rejection carrying that outcome, rather than
        // being mapped onto a friendlier neighbour.
        const message = dto.failureDetail
            || (dto.outcome === APG_ORION_OUTCOME_SUPERSEDED
                ? 'Superseded by a newer run from the same panel.'
                : 'The ORION run failed.');
        this.settle(runId, entry => entry.reject(makeApgOrionRejection(dto.outcome, message)));
    }

    // THE teardown choke point: remove the map entry, close the subscription, kill the timer — THEN
    // settle the promise. Every path funnels here, so a second settle for the same runId finds no
    // entry and is a no-op (idempotent), and nothing can leak on any outcome.
    private settle(runId: string, complete: (entry: PendingRun) => void): void {
        const entry = this._pending[runId];
        if (!entry) {
            return;   // already settled (or never registered)
        }
        delete this._pending[runId];
        entry.subscription.unsubscribe();
        clearTimeout(entry.timerHandle);
        complete(entry);
    }
}

// ---- Request mapping (domain -> wire) ---------------------------------------------------------

export function buildRunApgOrionCommand(runId: string, request: ApgOrionRunRequest): RunApgOrionSkill {
    return new RunApgOrionSkill(
        APG_ORION_CONTRACT_VERSION,
        runId,
        request.panelInstanceId,
        request.portfolioId,
        request.adjustmentPricingSettings || [],
        request.dateKind,
        request.date,
        request.rangeStart,
        request.rangeEnd,
        request.weekdayExclusions || [],
        request.specificDateExclusions || [],
        request.weekdayOverrides || [],
        null                                  // directionalBias — visible and inert in iteration 1
    );
}

// ---- Delivery mapping (wire -> the card the panel already renders) ----------------------------
//
// THE PROJECTION, AND THE ONE RULE BEHIND IT: every field the delivery does not carry is read off
// the card's OWN first adjustment — the same object, the same members, that `readCandidates()`
// reads off a Price Box cell today. The wire does not repeat them, so there is exactly one source
// for each fact and the card cannot disagree with the Price Box column beside it.
//
// `livePrice` is NOT set here and that is deliberate: the live accessor belongs to the Price Box
// cell, which the host owns. The orchestrator attaches it once the grid has populated from this
// same delivery, so the card's money ticks off the same getter the column binds.

/**
 * THE PRICE BOX'S FEED: the cards' adjustments and the no-card window cells', concatenated in
 * delivery order (leader ruling L37).
 *
 * ⚠ THREE THINGS THIS MUST NOT DO, each of which looks like an improvement:
 *
 *   1. **De-duplicate by date.** `windowAdjustments` is per CELL and its `date` is NOT unique
 *      (L50): a portfolio with no zone prices both #2B and #3 on one date, and one of the two may
 *      become a card while the other does not. A `Map`, a dictionary or a `find` on the date passes
 *      every ordinary run and drops a real column ONLY on a no-zone portfolio — the very state that
 *      already carries a `ZoneUnavailable` warning, so the missing column reads as part of that
 *      condition instead of as a bug.
 *   2. **Derive the window half from the cards.** It cannot be derived. A Monday or a report day
 *      never becomes a card; the array exists precisely to carry the columns the cards cannot.
 *   3. **Skip it when there are no cards.** A single report day yields an `empty` run whose column
 *      must still be drawn.
 *
 * ⚠ AND IT MUST COME BACK CHRONOLOGICAL. The grid renders its columns in ARRAY ORDER, so whatever
 * order this returns is the order the trader reads across the Price Box. The delivery's own order is
 * cards first — which is the run's RANKING — so returning it unsorted put the columns in ranked
 * order: 23-Sep, 18-Sep, 22-Sep, 17-Sep, 15-Sep, then the two non-card dates appended. That blocked
 * gate G1. The old `GetAdjustmentSolutions` reply happened to arrive in window order, so nothing on
 * this path ever had to sort before; now something does.
 */
export function toPriceBoxAdjustments(dto: ApgOrionCardsReadyDto | null): CashFlowAdjustment[] {
    const out: CashFlowAdjustment[] = [];
    if (!dto) {
        return out;
    }

    const cards = dto.cards || [];
    for (let ix = 0; ix < cards.length; ix++) {
        const adjustments = cards[ix] ? (cards[ix].adjustments || []) : [];
        for (let ax = 0; ax < adjustments.length; ax++) {
            out.push(adjustments[ax]);
        }
    }

    const window = dto.windowAdjustments || [];
    for (let wx = 0; wx < window.length; wx++) {
        const adjustments = window[wx] ? (window[wx].adjustments || []) : [];
        for (let ax = 0; ax < adjustments.length; ax++) {
            out.push(adjustments[ax]);
        }
    }

    return sortedByExpiration(out);
}

/**
 * The Price Box's column order: CHRONOLOGICAL by expiration.
 *
 * ⚠ THIS SORTS THE UNION AND NOTHING ELSE. `out` holds REFERENCES to the delivery's adjustment
 * objects, so re-ordering this array leaves `dto.cards` untouched — and it must. The card order IS
 * the run's ranking, and a tick may never re-order the cards (A3, G8). Never sort `Cards[]`, and
 * never derive a column order from one.
 *
 * ⚠ TIES ARE REAL AND THE SORT IS STABLE. A no-zone portfolio prices both #2B and #3 on one date
 * (L50), so two adjustments legitimately share an expiration; the incoming index is the last
 * comparison, so their relative order survives. `Array.sort` is not guaranteed stable on ES5
 * engines, which is why stability is written here rather than assumed.
 */
function sortedByExpiration(adjustments: CashFlowAdjustment[]): CashFlowAdjustment[] {
    const decorated: { item: CashFlowAdjustment; index: number; key: number | undefined }[] = [];
    for (let ix = 0; ix < adjustments.length; ix++) {
        decorated.push({
            item: adjustments[ix],
            index: ix,
            key: expirationSortKey(adjustments[ix])
        });
    }

    decorated.sort((a, b) => {
        if (a.key === undefined && b.key === undefined) {
            return a.index - b.index;
        }
        if (a.key === undefined) {
            return 1;                   // unparseable last, LOUDLY - see expirationSortKey
        }
        if (b.key === undefined) {
            return -1;
        }
        if (a.key !== b.key) {
            return a.key - b.key;
        }
        return a.index - b.index;       // stability, explicitly - never left to the engine's sort
    });

    const out: CashFlowAdjustment[] = [];
    for (let ix = 0; ix < decorated.length; ix++) {
        out.push(decorated[ix].item);
    }
    return out;
}

/**
 * `yyyy-MM-dd` -> a comparable number, or `undefined`.
 *
 * ⚠ PARSED EXPLICITLY, NOT COMPARED AS A STRING ON FAITH. This codebase carries TWO expiration
 * formats: `CashFlowAdjustment.expiration` is `yyyy-MM-dd` (the checklist service parses it that way,
 * off a `CashFlowAdjustment`), while the Price Box LABEL and the order-ticket legs use `dd-MMM-yy`.
 * A lexicographic compare would read the right format correctly and the wrong one plausibly —
 * '18-Sep-26' sorts before '23-Sep-26' by luck, and after '05-Oct-26' by the same luck — so the
 * columns would come out right until the month rolled.
 *
 * ⚠ AN UNPARSEABLE VALUE IS REPORTED, NEVER TOLERATED QUIETLY. It still sorts to one end, because
 * dropping it would blank a Price Box column the trader is meant to see — but it says so. A value
 * that cannot be placed, sorted silently to an end, is the exact shape of a defect that looks like a
 * layout preference.
 */
function expirationSortKey(adjustment: CashFlowAdjustment): number | undefined {
    const raw = adjustment ? adjustment.expiration : null;
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw || '');
    if (!match) {
        console.error(
            '[orion] DEFECT: an adjustment\'s expiration "' + (raw === null ? 'null' : raw) +
            '" is not the yyyy-MM-dd form CashFlowAdjustment.expiration carries. Its Price Box ' +
            'column cannot be placed chronologically and is pushed to the end of the row. ' +
            'Do not treat this as cosmetic - a date the client cannot read is a date it cannot rank.');
        return undefined;
    }
    return (+match[1]) * 10000 + (+match[2]) * 100 + (+match[3]);
}

export function toSuggestionContents(cards: ApgOrionCardDto[] | null | undefined): RollSuggestionContent[] {
    const list = cards || [];
    const out: RollSuggestionContent[] = [];
    for (let ix = 0; ix < list.length; ix++) {
        const content = toSuggestionContent(list[ix]);
        if (content) {
            out.push(content);
        }
    }
    return out;
}

export function toSuggestionContent(card: ApgOrionCardDto): RollSuggestionContent | null {
    if (!card) {
        return null;
    }

    const adjustments: CashFlowAdjustment[] = card.adjustments || [];
    const first = adjustments.length > 0 ? adjustments[0] : null;
    if (!first) {
        // A card with no adjustment cannot be previewed, reviewed or priced. Dropped and reported —
        // a silently absent card is the failure class this whole feature exists to avoid.
        console.error('[orion] DEFECT: a delivered card for ' + card.date + ' carries no adjustment. It is not shown.');
        return null;
    }

    const price = isValidNumber(card.price) ? (card.price as number) : null;
    const direction: CostDirection = (price !== null && price < 0) ? 'debit' : 'credit';

    const types = card.adjustmentTypes || [];
    const type1 = types.length > 0 ? types[0] : first.adjustmentType;
    const type2 = types.length > 1 ? types[1] : null;

    return {
        // THE ORDERING FIGURE, as the run ranked on it. Data, not display: the results toolbar
        // re-sorts on this exact number rather than re-deriving one from the rounded money, so
        // there is never a second definition of "better".
        perDayCredit: isValidNumber(card.perDayCredit) ? (card.perDayCredit as number) : undefined,
        adjustmentDate: first.expirationPretty,
        daysOut: card.dte,
        adjustmentTypes: types,
        currentStrike: first.initialStrike,
        destinationStrike: first.destinationStrike,
        direction: first.direction,
        costDirection: direction,
        // The frozen snapshot. The card prefers the live accessor the orchestrator attaches; this
        // is what a reader without one sees.
        cost: price !== null ? Math.abs(price) : 0,
        expiration: first.expiration,
        template: first.templateName,
        economicReports: card.economicReports || [],
        zone: null,
        summary: card.reason,
        // The delivery ships the reason as ONE finished sentence (A1 §5). The card's live-money
        // composition needs the two halves, so the sentence is split at the money the backend baked
        // in; when it cannot be split the card falls back to `summary`, which is the same text.
        reason: splitReason(card.reason),
        previewKey: {
            adjustmentType: type1,
            adjustmentType2: type2,
            expirationDatePretty: first.expirationPretty
        },
        adjustments: adjustments
    };
}

// The reason line's two halves, so the card can compose the sentence around the LIVE figure the
// header shows rather than the run-time one the backend baked in. The backend writes
//     lead + ("collects a $" | "pays a $") + amount + (" credit" | " debit") + tail
// verbatim from the browser's own composer, so the money clause is what separates the halves.
//
// ⚠ FAILS TO `undefined`, NEVER TO A GUESS. An unrecognised sentence leaves the card on `summary`,
// which is the same text with the run-time money in it — stale by a tick, but never wrong.
const REASON_MONEY_CLAUSE = /^([\s\S]*?)(?:collects a \$|pays a \$)[0-9.,-]+ (?:credit|debit)([\s\S]*)$/;

export function splitReason(reason: string | null | undefined): { lead: string; tail: string } | undefined {
    if (!reason) {
        return undefined;
    }
    const match = REASON_MONEY_CLAUSE.exec(reason);
    return match ? { lead: match[1], tail: match[2] } : undefined;
}
